import CompareIcon from '@mui/icons-material/Compare'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import { alpha, Box, Chip, IconButton, Skeleton, Tooltip, Typography, useTheme } from '@mui/material'
import { useDealData } from 'api/aws/deals/useDealData'
import { useEntitiesFromSearch, useSearchData } from 'api/search'
import { renderColorByString } from 'components/DataGrid/renderColorByString'
import PandaDocLinkIcon from 'components/Icons/PandaDockLinkIcon'
import DealDetailsLoading from 'components/Loading/DealDetailsLoading'
import { useEffect, useMemo, useState } from 'react'
import { Outlet, useNavigate, useParams } from 'react-router-dom'
import { PageContainer, TabConfig, TabNavigation } from 'theme/components'
import { buildMasterAgreements } from 'utils/agreementVersioning'
import { useStrategy } from '../../../api/aws/useStrategy'
import { useTimeline } from '../../../api/aws/useTimeline'
import { useLayoutContext } from '../../../context/LayoutProvider/LayoutProvider'
import { useValidationContext } from '../../../context/ValidationProvider/ValidationProvider'
import DealComparisonModal from '../DealComparisonModal'

const DealRouting = () => {
  const { PK, SK } = useParams()
  const dealId = SK?.split('DEAL_')?.[1]
  const navigate = useNavigate()
  const { pinned } = useLayoutContext()
  const { deal, productRows, organization } = useDealData(PK, SK, dealId)
  const { strategy, isLoading: isStrategyLoading } = useStrategy(PK ?? null, SK ?? null, ['strategy', dealId ?? null])
  const { errorKeys, handleValidateStrategy, isStrategyValid } = useValidationContext()
  useTimeline(dealId) // Pre-fetch timeline data for child routes
  const theme = useTheme()
  const { isLoading } = useSearchData()

  // Deal comparison modal state
  const [comparisonAnchor, setComparisonAnchor] = useState<HTMLElement | null>(null)
  const isComparisonOpen = Boolean(comparisonAnchor)

  // Build version chain for comparison modal
  const { deals, products: allProducts } = useEntitiesFromSearch()
  const { comparisonDeal, comparisonProducts, currentVersionLabel, comparisonVersionLabel } = useMemo(() => {
    const empty = {
      comparisonDeal: null,
      comparisonProducts: [] as any[],
      currentVersionLabel: '',
      comparisonVersionLabel: '',
    }
    if (!PK || !deal?.deal_uuid) return empty

    const orgDeals = deals?.filter(d => d?.PK === PK) ?? []
    const orgProducts = allProducts?.filter(p => p?.PK === PK) ?? []
    const { agreements } = buildMasterAgreements(orgDeals, orgProducts)

    const match =
      (deal.masterAgreementId ? agreements.find(a => a.masterAgreementId === deal.masterAgreementId) : null) ??
      agreements.find(a => a.versions.some(v => v.deal?.deal_uuid === deal.deal_uuid))

    if (match && match.versions.length > 1) {
      const idx = match.versions.findIndex(v => v.deal?.deal_uuid === deal.deal_uuid)
      if (idx > 0) {
        const prevVersion = match.versions[idx - 1]
        const currentVersion = match.versions[idx]
        return {
          comparisonDeal: prevVersion.deal,
          comparisonProducts: prevVersion.products,
          currentVersionLabel: `v${currentVersion.semanticVersion}`,
          comparisonVersionLabel: `v${prevVersion.semanticVersion}`,
        }
      }
    }

    // Fallback: legacy deals linked via predecessorId
    if (deal.predecessorId) {
      const predecessor = orgDeals.find(d => d?.deal_uuid === deal.predecessorId)
      if (predecessor) {
        const predProducts = orgProducts.filter(p => p?.deal_uuid === predecessor.deal_uuid)
        return {
          comparisonDeal: predecessor,
          comparisonProducts: predProducts,
          currentVersionLabel: deal.semanticVersion ? `v${deal.semanticVersion}` : 'Current',
          comparisonVersionLabel: predecessor.semanticVersion ? `v${predecessor.semanticVersion}` : 'Previous',
        }
      }
    }

    return empty
  }, [PK, deal?.deal_uuid, deal?.masterAgreementId, deal?.predecessorId, deals, allProducts])

  useEffect(() => {
    if (deal && strategy && !isStrategyLoading) {
      handleValidateStrategy(strategy, deal, productRows)
    }
  }, [deal, strategy])

  const isDealWon = deal?.stage === 'Deal Won'

  // Primary tabs — the core workflow tabs always visible
  const tabs: TabConfig[] = useMemo(
    () => [
      {
        label: 'Agreement',
        path: 'agreement',
        customLabel: (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography noWrap sx={{ fontSize: 13 }}>
              Agreement
            </Typography>
            {deal?.contractId && (
              <Box
                onClick={e => {
                  e.preventDefault()
                  e.stopPropagation()
                  window.open(`https://app.pandadoc.com/a/#/documents/${deal.contractId}`, '_blank')
                }}
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              >
                <PandaDocLinkIcon size={16} />
              </Box>
            )}
          </Box>
        ),
      },
      { label: 'Details', path: 'details' },
      // { label: 'Services', path: 'services' },
      ...(strategy ? [{ label: 'Strategy', path: 'strategy', disabled: true }] : []),
      { label: 'Review', path: 'review' },
      { label: 'Comments', path: 'comments' },
      { label: 'Schedule', path: 'schedule' },
    ],
    [deal?.contractId, strategy]
  )

  // Overflow tabs — less frequent, behind "More" menu
  const overflowTabs: TabConfig[] = useMemo(
    () => [
      ...(isDealWon ? [{ label: 'Invoicing', path: 'invoicing' }] : []),
      { label: 'Timeline', path: 'timeline' },
      ...(deal?.semanticVersion || deal?.masterAgreementId ? [{ label: 'Versions', path: 'versions' }] : []),
      { label: 'Heartbeat', path: 'heartbeat' },
    ],
    [isDealWon, deal?.semanticVersion, deal?.masterAgreementId]
  )

  // Stage color
  const stageColor = deal?.stage ? renderColorByString(deal.stage) : theme.palette.text.secondary

  return (
    <>
      {/* Compact context bar — replaces PageHeader */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 0.75,
          pt: pinned ? '60px' : 0.75,
          ml: 5,
          transition: 'padding 0.3s ease',
          minHeight: 36,
        }}
      >
        {isLoading || isStrategyLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Skeleton variant="text" width={120} height={20} />
            <Skeleton variant="text" width={200} height={20} />
          </Box>
        ) : (
          <>
            {/* Left: breadcrumb — Org › Deal Name · version · stage */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, overflow: 'hidden' }}>
              {/* Org name — clickable back to org */}
              <Typography
                onClick={() => organization?.PK && navigate(`/organizations/${organization.PK}/details`)}
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: 'text.secondary',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  '&:hover': { color: 'text.primary' },
                }}
              >
                {organization?.name || ''}
              </Typography>

              <NavigateNextIcon sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />

              {/* Deal name */}
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'text.primary',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {deal?.name}
              </Typography>

              {/* Version badge */}
              {deal?.semanticVersion && (
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 500,
                    color: 'text.disabled',
                    ml: 0.5,
                    whiteSpace: 'nowrap',
                  }}
                >
                  v{deal.semanticVersion}
                </Typography>
              )}

              {/* Stage chip */}
              {deal?.stage && (
                <Chip
                  label={deal.stage}
                  size="small"
                  sx={{
                    ml: 1,
                    height: 20,
                    fontSize: 11,
                    fontWeight: 600,
                    color: stageColor,
                    bgcolor: alpha(theme.palette.text.primary, 0.04),
                    border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                  }}
                />
              )}

              {/* Deal type */}
              {deal?.type && (
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 500,
                    color: 'text.disabled',
                    ml: 0.5,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {deal.type}
                </Typography>
              )}
            </Box>

            {/* Right: compare + any contextual actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              {comparisonDeal && (
                <Tooltip title={`Compare with ${comparisonVersionLabel}`}>
                  <IconButton
                    size="small"
                    onClick={e => setComparisonAnchor(e.currentTarget)}
                    sx={{ width: 28, height: 28 }}
                  >
                    <CompareIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </>
        )}
      </Box>

      {/* Deal Comparison Modal */}
      <DealComparisonModal
        currentDeal={deal}
        currentDealProducts={productRows}
        comparisonDeal={comparisonDeal}
        comparisonDealProducts={comparisonProducts}
        currentVersionLabel={currentVersionLabel}
        comparisonVersionLabel={comparisonVersionLabel}
        open={isComparisonOpen}
        onClose={() => setComparisonAnchor(null)}
        anchorEl={comparisonAnchor}
      />

      <TabNavigation tabs={tabs} overflowTabs={overflowTabs} basePath={`/deals/${PK}/${SK}`} scrollable />

      {isStrategyLoading ? (
        <DealDetailsLoading />
      ) : (
        <PageContainer height="calc(100vh - 80px)">
          <Outlet context={{ pinned }} />
        </PageContainer>
      )}
    </>
  )
}

export default DealRouting
